import type { AxiosResponse } from 'axios';
import { flow } from 'fp-ts/function';
import * as E from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import * as TE from 'fp-ts/TaskEither';
import type * as t from 'io-ts';
import type { z } from 'zod';

import type { BSError } from '../../model/error/BSError';
import { BSErrorType } from '../../model/error/BSError';
import type { BSTask } from '../../model/types';

const TODecodeError = {
  type: BSErrorType.InvalidResponseFormatError,
  userFriendlyTitle: 'Server error',
  userFriendlyExplanation: 'Invalid data received',
};

export const decodeWith = <A>(decoder: t.Decoder<unknown, A>) =>
  flow(
    decoder.decode,
    E.mapLeft((): BSError => TODecodeError),
    TE.fromEither,
  );

export const responseDecoder =
  <A>(decoder: t.Decoder<unknown, A>) =>
  (response: BSTask<AxiosResponse<unknown>>) =>
    pipe(
      response,
      TE.map((x: AxiosResponse<unknown>) => x.data),
      TE.chain<BSError, unknown, A>(decodeWith(decoder)),
    );

export const decodeWithZod =
  <A>(schema: z.ZodSchema<A>) =>
  (input: unknown): TE.TaskEither<BSError, A> => {
    const result = schema.safeParse(input);

    if (result.success) {
      return TE.right(result.data);
    } else {
      return TE.left(TODecodeError);
    }
  };

export const responseZodDecoder =
  <A>(schema: z.ZodSchema<A>) =>
  (response: BSTask<AxiosResponse<unknown>>) =>
    pipe(
      response,
      TE.map((x) => x.data),
      TE.chain(decodeWithZod(schema)),
    );
